import { useState, useEffect } from 'react';
import { User, Loader2, Plus, Minus, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { toast } from 'sonner';
import { getAuthToken, updateUserData, getActivePortal } from '@/utils/auth';
import { cn } from '@/lib/utils';
import { PhoneInputWithCountry } from '@/components/ui/PhoneInputWithCountry';
import { validateE164Phone } from '@/constants/countryCodes';

import { useDiscovery } from '@/context/DiscoveryContext';
import { formatBoardLabel } from '@/utils/boardUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export function ProfileCompletionModal({ isOpen, onComplete, onClose, currentProfile }) {
  const { boards, grades, loadingBoardsGrades } = useDiscovery();
  const [fullName, setFullName] = useState(currentProfile?.full_name || '');
  // In the student portal, only 'student', 'teacher', and 'guardian' are valid user types.
  // If the account was created as an 'affiliator' (admin-created), treat them as a student
  // during profile completion so they can set board/grade correctly.
  const rawUserType = currentProfile?.user_type;
  const userType = rawUserType === 'guardian' ? 'guardian' : ((rawUserType === 'teacher') ? 'teacher' : 'student');
  const [boardId, setBoardId] = useState(currentProfile?.board?.id || '');
  const [gradeId, setGradeId] = useState(currentProfile?.grade?.id || '');
  const [phoneNo, setPhoneNo] = useState(currentProfile?.phone_no || '');
  const [inviteCode, setInviteCode] = useState('');
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customCurriculum, setCustomCurriculum] = useState('');

  const OTHERS_GRADES = Array.from({ length: 12 }, (_, i) => ({ id: `custom_${i + 1}`, grade_name: `Grade ${i + 1}` }));

  // Sync with currentProfile if it changes while open
  useEffect(() => {
    if (currentProfile) {
      if (!fullName) setFullName(currentProfile.full_name || '');
      if (!boardId) setBoardId(currentProfile.board?.id || '');
      if (!gradeId) setGradeId(currentProfile.grade?.id || '');
      if (!phoneNo) setPhoneNo(currentProfile.phone_no || '');
    }
  }, [currentProfile, fullName, boardId, gradeId, phoneNo]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Teachers don't need a grade, guardians don't need grade or board but DO need phone number
    const needsBoardGrade = userType !== 'guardian';
    const isCustom = boardId === 'other' || String(gradeId).startsWith('custom_');

    if (!fullName || (userType === 'guardian' && !phoneNo?.trim())) {
      toast.error('Please fill in all required fields including your phone number');
      return;
    }

    if (userType === 'student') {
      if (!phoneNo?.trim()) {
        toast.error("Please enter your parent's WhatsApp number");
        return;
      }
      const phoneValidation = validateE164Phone(phoneNo);
      if (!phoneValidation.valid) {
        toast.error(phoneValidation.error || "Please enter a valid parent's WhatsApp number in international format");
        return;
      }
    }

    if (needsBoardGrade) {
      if (boardId === 'other' && !customCurriculum.trim()) {
        toast.error('Please enter your curriculum name');
        return;
      }
      if (!boardId) {
        toast.error('Please select a curriculum board');
        return;
      }
      if (userType === 'student') {
        if (!gradeId) {
          toast.error('Please select a grade');
          return;
        }
      }
    }

    setLoading(true);
    try {
      const portal = getActivePortal() || 'student';
      const token = getAuthToken(portal);

      const payload = {
        full_name: fullName || currentProfile?.email?.split('@')[0],
        user_type: userType,
      };

      if (userType === 'guardian') {
        if (phoneNo.trim()) {
          payload.phone_no = phoneNo.trim();
        }
      } else if (userType === 'student') {
        if (phoneNo?.trim()) {
          const phoneValidation = validateE164Phone(phoneNo);
          if (phoneValidation.valid) {
            payload.phone_no = phoneValidation.sanitized;
          }
        }
      }
      if (isCustom) {
        payload.is_custom_curriculum = true;
        const curName = boardId === 'other' ? customCurriculum.trim() : (boards.find(b => String(b.id) === String(boardId))?.board_name || 'CBSE');
        const grdName = String(gradeId).replace('custom_', '');
        payload.custom_curriculum_name = curName;
        payload.custom_grade = grdName;
        payload.board_id = 1;
        payload.grade_id = 5;
      } else {
        payload.board_id = parseInt(boardId);
        if (userType === 'student') {
          payload.grade_id = parseInt(gradeId);
        }
      }

      const response = await axios.patch(
        apiUrl('users/profile/'),
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Update local storage with new profile data
      const updateData = {
        full_name: payload.full_name,
        user_type: userType,
      };

      if (userType === 'guardian') {
        if (payload.phone_no) {
          updateData.phone_no = payload.phone_no;
        }
      } else if (userType === 'student') {
        if (payload.phone_no) {
          updateData.phone_no = payload.phone_no;
          updateData.parent_whatsapp_number = payload.phone_no;
        }
      }
      if (isCustom) {
        updateData.is_custom_curriculum = true;
        updateData.requested_curriculum = `${payload.custom_curriculum_name} (Grade ${payload.custom_grade})`.trim();
        updateData.board = { id: 1, board_name: 'Central Board (Preview)' };
        updateData.grade = { id: 5, grade_name: 'Grade 10 (Preview)' };
      } else {
        updateData.board = { id: parseInt(boardId) };
        if (userType === 'student') {
          updateData.grade = { id: parseInt(gradeId) };
        }
      }

      updateUserData(portal, updateData);

      // Set board and grade in localStorage for students
      if (userType === 'student') {
        localStorage.setItem('student_board_id', isCustom ? '1' : String(boardId));
        localStorage.setItem('student_grade_id', isCustom ? '5' : String(gradeId));
        window.dispatchEvent(new Event('storage'));
      }

      // Claim institution invite code if provided
      if (userType === 'student' && inviteCode.trim()) {
        try {
          const claimResponse = await axios.post(
            apiUrl('superadmin/b2b/enroll/claim_invite/'),
            { invite_code: inviteCode.trim() },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          toast.success(`Successfully joined ${claimResponse.data.profile?.institute?.name || 'institution'}!`);
        } catch (claimError) {
          console.error('Failed to claim institution invite code:', claimError);
          const errorMsg = claimError.response?.data?.detail || claimError.response?.data?.error || 'Invalid invite code or email not whitelisted';
          toast.warning(`Profile completed, but institution link failed: ${errorMsg}`);
        }
      }

      toast.success('Profile completed successfully!');
      onComplete();

    } catch (error) {
      console.error('Profile completion error:', error);
      const resData = error.response?.data;
      if (resData?.error) {
        toast.error(resData.error);
      } else if (resData?.detail) {
        toast.error(resData.detail);
      } else if (resData && typeof resData === 'object') {
        const firstKey = Object.keys(resData)[0];
        const firstVal = resData[firstKey];
        const msg = Array.isArray(firstVal) ? firstVal[0] : (typeof firstVal === 'string' ? firstVal : 'Failed to complete profile. Please try again.');
        toast.error(`${firstKey !== 'non_field_errors' ? `${firstKey}: ` : ''}${msg}`);
      } else {
        toast.error('Failed to complete profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkipForNow = async () => {
    const needsBoardGrade = userType !== 'guardian';
    const isCustom = boardId === 'other' || String(gradeId).startsWith('custom_');

    if (!fullName) {
      toast.error('Please enter your full name');
      return;
    }

    if (needsBoardGrade) {
      if (boardId === 'other' && !customCurriculum.trim()) {
        toast.error('Please enter your curriculum name');
        return;
      }
      if (!boardId) {
        toast.error('Please select a curriculum board');
        return;
      }
      if (userType === 'student' && !gradeId) {
        toast.error('Please select a grade');
        return;
      }
    }

    setLoading(true);
    try {
      const portal = getActivePortal() || 'student';
      const token = getAuthToken(portal);

      const payload = {
        full_name: fullName || currentProfile?.email?.split('@')[0],
        user_type: userType,
      };

      if (isCustom) {
        payload.is_custom_curriculum = true;
        const curName = boardId === 'other' ? customCurriculum.trim() : (boards.find(b => String(b.id) === String(boardId))?.board_name || 'CBSE');
        const grdName = String(gradeId).replace('custom_', '');
        payload.custom_curriculum_name = curName;
        payload.custom_grade = grdName;
        payload.board_id = 1;
        payload.grade_id = 5;
      } else {
        payload.board_id = parseInt(boardId);
        if (userType === 'student') {
          payload.grade_id = parseInt(gradeId);
        }
      }

      await axios.patch(
        apiUrl('users/profile/'),
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const updateData = {
        full_name: payload.full_name,
        user_type: userType,
      };

      if (isCustom) {
        updateData.is_custom_curriculum = true;
        updateData.requested_curriculum = `${payload.custom_curriculum_name} (Grade ${payload.custom_grade})`.trim();
        updateData.board = { id: 1, board_name: 'Central Board (Preview)' };
        updateData.grade = { id: 5, grade_name: 'Grade 10 (Preview)' };
      } else {
        updateData.board = { id: parseInt(boardId) };
        if (userType === 'student') {
          updateData.grade = { id: parseInt(gradeId) };
        }
      }

      updateUserData(portal, updateData);

      if (userType === 'student') {
        localStorage.setItem('student_board_id', isCustom ? '1' : String(boardId));
        localStorage.setItem('student_grade_id', isCustom ? '5' : String(gradeId));
        sessionStorage.setItem('parent_phone_grace_used', 'true');
        window.dispatchEvent(new Event('storage'));
      }

      toast.info("Academic details saved! You can add your parent's WhatsApp number in your next session.");
      onComplete();
    } catch (err) {
      console.error('Failed to save academic profile:', err);
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className={cn(
        "bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] shadow-2xl transition-all duration-300 outline-none flex flex-col p-0 rounded-2xl relative",
        "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-1.5rem)] h-auto max-h-[calc(100dvh-(env(safe-area-inset-top)+env(safe-area-inset-bottom)+9rem))] overflow-y-auto overflow-x-hidden",
        "md:max-h-[85vh] md:max-w-lg md:w-full"
      )}>


        <div className="p-6">
          {/* Header */}
          <div className="mb-6 text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">Complete Your Profile</h2>
            <p className="text-base text-muted-foreground">
              Let's personalize your learning experience
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="block w-full px-4 py-2.5 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground"
                placeholder="Enter your full name"
                required
                disabled={loading}
              />
            </div>

            {/* Board */}
            {userType !== 'guardian' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Board
                </label>
                <Select
                  value={boardId ? String(boardId) : ""}
                  onValueChange={(val) => {
                    setBoardId(val);
                    setGradeId(''); // Reset grade whenever board changes
                  }}
                  disabled={loadingBoardsGrades || loading}
                >
                  <SelectTrigger className="w-full h-11 px-4 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-xl text-foreground text-sm font-normal focus:ring-2 focus:ring-[hsl(var(--green-bright))]">
                    <SelectValue placeholder={loadingBoardsGrades ? "Loading..." : "Select curriculum"} />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border text-popover-foreground z-[15000] max-h-72 w-full min-w-[var(--radix-select-trigger-width)]">
                    {boards.map((board) => (
                      <SelectItem key={board.id} value={String(board.id)}>
                        {formatBoardLabel(board)}
                      </SelectItem>
                    ))}
                    <SelectItem value="other">+ Other / Not Listed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Custom Curriculum Input */}
            {boardId === 'other' && (
              <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                <label className="text-sm font-medium text-foreground">Specify Curriculum / Board <span className="text-emerald-500">*</span></label>
                <input
                  type="text"
                  value={customCurriculum}
                  onChange={(e) => setCustomCurriculum(e.target.value)}
                  className="block w-full px-4 py-2.5 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground"
                  placeholder="e.g. ICSE, IGCSE, State Board, IB..."
                  required
                  disabled={loading}
                />
              </div>
            )}

            {/* Phone Number - Required for Guardians */}
            {userType === 'guardian' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Phone Number <span className="text-red-500">*</span></label>
                <PhoneInputWithCountry
                  value={phoneNo}
                  onChange={(val) => setPhoneNo(val)}
                  disabled={loading}
                  required={true}
                  placeholder="9876543210"
                  selectClassName="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] rounded-lg text-foreground font-semibold py-2.5"
                  inputClassName="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground py-2.5"
                />
              </div>
            )}

            {/* Grade - Only for Students */}
            {userType === 'student' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Grade
                </label>
                <Select
                  value={gradeId ? String(gradeId) : ""}
                  onValueChange={setGradeId}
                  disabled={(loadingBoardsGrades && boardId !== 'other') || loading}
                >
                  <SelectTrigger className="w-full h-11 px-4 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-xl text-foreground text-sm font-normal focus:ring-2 focus:ring-[hsl(var(--green-bright))]">
                    <SelectValue placeholder={loadingBoardsGrades && boardId !== 'other' ? "Loading..." : "Select grade"} />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border text-popover-foreground z-[15000] max-h-72 w-full min-w-[var(--radix-select-trigger-width)]">
                    {boardId === 'other' ? (
                      OTHERS_GRADES.map((g) => (
                        <SelectItem key={g.id} value={g.id}>{g.grade_name}</SelectItem>
                      ))
                    ) : (
                      <>
                        {grades.map((grade) => (
                          <SelectItem key={grade.id} value={String(grade.id)}>
                            {grade.grade_name}
                          </SelectItem>
                        ))}
                        <SelectItem value="custom_11">Grade 11</SelectItem>
                        <SelectItem value="custom_12">Grade 12</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Parent's WhatsApp Number - Only for Students */}
            {userType === 'student' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Parent's WhatsApp Number
                </label>
                <PhoneInputWithCountry
                  value={phoneNo}
                  onChange={(val) => setPhoneNo(val)}
                  disabled={loading}
                  placeholder="Enter mobile number"
                  selectClassName="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] rounded-lg text-foreground font-semibold py-2.5"
                  inputClassName="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground py-2.5"
                />
                <p className="text-xs text-muted-foreground flex items-start gap-1">
                  <span className="text-emerald-500 font-bold">*</span>
                  <span>Receive important updates about your learning progress, tuition bookings and upcoming sessions.</span>
                </p>
              </div>
            )}

            {/* Institution Invite Code - Only for Students */}
            {userType === 'student' && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setShowInviteCode(!showInviteCode)}
                  className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span>Do you have an Institution Invite code?</span>
                  {showInviteCode ? (
                    <Minus className="h-4 w-4 text-[hsl(var(--green-bright))]" />
                  ) : (
                    <Plus className="h-4 w-4 text-[hsl(var(--green-bright))]" />
                  )}
                </button>
                {showInviteCode && (
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    className="block w-full px-4 py-2.5 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded-lg focus:ring-2 focus:ring-[hsl(var(--green-bright))] text-foreground uppercase placeholder:normal-case select-text cursor-text font-semibold tracking-wider text-center"
                    placeholder="e.g. BUD-INST-7C89"
                    disabled={loading}
                  />
                )}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white py-2 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Complete Profile'
              )}
            </button>

            {/* Subtle skip link for students */}
            {userType === 'student' && (
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={handleSkipForNow}
                  disabled={loading}
                  className="text-xs text-muted-foreground/75 hover:text-foreground transition-colors hover:underline underline-offset-4"
                >
                  I don't have it right now — remind me next session
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
